import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { fetchTrip, updateStatus, STATUSES, assignDriver, unassignDriver, DRIVERS } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { DestinationBadge } from "../components/DestinationBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft, Plane, Hotel, MapPin, User, Mail, Phone, Clock, Check,
  FileText, Activity, AlertCircle, MessageCircleMore,
  Car, Trash2, Save,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { cn } from "../lib/utils";

export default function AdminDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changingStatus, setChangingStatus] = useState(false);

  const load = async () => {
    try {
      const t = await fetchTrip(id);
      setTrip(t);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const changeStatus = async (status) => {
    setChangingStatus(true);
    try {
      const updated = await updateStatus(id, { status, actor: "admin" });
      setTrip(updated);
      toast.success(`Status set to ${status}`);
    } catch (e) { toast.error("Failed to update status"); } finally { setChangingStatus(false); }
  };

  if (loading) return <div className="p-8 text-zinc-400 text-center">Loading trip...</div>;
  if (!trip) return <div className="p-8 text-zinc-400 text-center">Trip not found.</div>;

  return (
    <div className="min-h-[calc(100vh-4rem)] px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-testid="admin-detail-page">
      <div className="max-w-[1400px] mx-auto space-y-6">
        <Link to="/admin/records" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="back-link">
          <ArrowLeft className="h-4 w-4" /> Back to records
        </Link>

        {/* Hero */}
        <div className="glass-card p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl opacity-20"
            style={{ background: trip.destination === "Monterrey" ? "#10B981" : "#F59E0B" }} />
          <div className="relative">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <DestinationBadge destination={trip.destination} size="lg" />
                  <StatusBadge status={trip.status} />
                  {trip.tags?.map((t) => (
                    <span key={t} className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-white/5 border border-white/10 text-zinc-300">{t}</span>
                  ))}
                </div>
                <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-1" data-testid="trip-traveler-name">{trip.full_name}</h1>
                <p className="text-sm text-zinc-400">{trip.purpose || `Trip to ${trip.destination}`}</p>
              </div>
              <div className="text-right">
                <p className="label-eyebrow">Arrival</p>
                <p className="font-heading text-lg text-white mt-1">{format(parseISO(trip.arrival_date), "MMM d, yyyy")} {trip.arrival_time}</p>
                <p className="label-eyebrow mt-3">Departure</p>
                <p className="font-heading text-lg text-white mt-1">{format(parseISO(trip.departure_date), "MMM d, yyyy")} {trip.departure_time}</p>
              </div>
            </div>

            {/* Status actions bar */}
            <div className="mt-6 pt-5 border-t border-white/10">
              <p className="label-eyebrow mb-2.5">Change status</p>
              <div className="flex gap-1.5 flex-wrap" data-testid="status-actions">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => changeStatus(s)}
                    disabled={changingStatus || trip.status === s}
                    data-testid={`status-action-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-xs font-semibold border transition-all disabled:opacity-50",
                      trip.status === s
                        ? "bg-[#F07D00] border-[#F07D00] text-white cursor-default"
                        : "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10 hover:border-white/20"
                    )}
                  >
                    {trip.status === s && <Check className="h-3 w-3 inline mr-1" />}
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="bg-white/5 border border-white/10 p-1 backdrop-blur-xl" data-testid="detail-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="transport" data-testid="tab-transport">
              <Car className="h-3.5 w-3.5 mr-1.5" /> Driver
              {trip.driver_name && <span className="ml-1.5 h-1.5 w-1.5 rounded-full bg-emerald-400" />}
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">
              <Activity className="h-3.5 w-3.5 mr-1.5" /> Audit
            </TabsTrigger>
            <TabsTrigger value="files" data-testid="tab-files">
              <FileText className="h-3.5 w-3.5 mr-1.5" /> Files
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <DetailPanel icon={User} title="Personal">
                <Row label="Name" value={trip.full_name} />
                <Row label="Email" value={trip.email} icon={Mail} />
                <Row label="Phone" value={trip.phone || "—"} icon={Phone} />
                <Row label="WhatsApp" value={trip.whatsapp || "—"} icon={MessageCircleMore} />
                <Row label="Emergency" value={trip.emergency_contact || "—"} />
              </DetailPanel>
              <DetailPanel icon={Plane} title="Flight">
                <Row label="Airline" value={trip.airline || "—"} />
                <Row label="Flight #" value={trip.flight_number || "—"} />
                <Row label="Origin" value={trip.origin_city || "—"} />
                <Row label="Airport" value={trip.destination_airport || "—"} />
                <Row label="Transport" value={trip.transportation_notes || "—"} />
              </DetailPanel>
              <DetailPanel icon={Hotel} title="Lodging">
                <Row label="Property" value={trip.accommodation_name || "—"} />
                <Row label="Type" value={trip.accommodation_type || "—"} />
                <Row label="Address" value={trip.accommodation_address || "—"} />
              </DetailPanel>
              <DetailPanel icon={MapPin} title="Locations">
                <Row label="Primary" value={trip.primary_location || "—"} />
                {trip.expected_locations?.length > 0 && trip.expected_locations.map((loc, i) => (
                  <Row key={i} label={loc.type} value={`${loc.name}${loc.date ? ` · ${format(parseISO(loc.date), "MMM d")}` : ""}`} />
                ))}
                {(!trip.expected_locations || trip.expected_locations.length === 0) && <Row label="Other" value="—" />}
              </DetailPanel>
              <DetailPanel icon={Car} title="Ground Transport">
                {trip.driver_name ? (
                  <>
                    <Row label="Driver" value={trip.driver_name} />
                    <Row label="Phone" value={trip.driver_phone || "—"} icon={Phone} />
                    <Row label="Vehicle" value={trip.driver_vehicle || "—"} />
                    <Row label="Plates" value={trip.driver_license_plate || "—"} />
                    {trip.driver_assigned_at && (
                      <Row label="Assigned" value={format(new Date(trip.driver_assigned_at), "MMM d · HH:mm")} icon={Clock} />
                    )}
                  </>
                ) : (
                  <div className="text-xs text-zinc-500 py-1">No driver assigned. Open the <span className="text-[#F07D00] font-semibold">Driver</span> tab to assign one.</div>
                )}
              </DetailPanel>
              {trip.special_notes && (
                <div className="glass-card p-5 lg:col-span-2" data-testid="special-notes">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-4 w-4 text-[#F07D00]" />
                    <h3 className="font-heading text-sm font-semibold tracking-wider uppercase text-zinc-300">Special notes</h3>
                  </div>
                  <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{trip.special_notes}</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="transport" className="mt-4">
            <DriverSection trip={trip} onSaved={load} />
          </TabsContent>

          <TabsContent value="audit" className="mt-4">
            <div className="glass-card p-5 sm:p-6" data-testid="audit-log">
              <p className="label-eyebrow mb-4">Audit trail · {trip.audit_log?.length || 0} events</p>
              <div className="space-y-2">
                {trip.audit_log?.slice().reverse().map((a) => (
                  <div key={a.id} className="flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/10" data-testid={`audit-${a.id}`}>
                    <div className="h-8 w-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Activity className="h-3.5 w-3.5 text-[#F07D00]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-white">{a.action.replace(/_/g, " ")}</span>
                        <span className="text-[10px] tracking-wider uppercase text-zinc-500">by {a.actor}</span>
                      </div>
                      {a.details && <p className="text-xs text-zinc-400 mt-0.5 truncate">{a.details}</p>}
                    </div>
                    <span className="text-[10px] text-zinc-500 whitespace-nowrap">{format(new Date(a.timestamp), "MMM d · HH:mm")}</span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="files" className="mt-4">
            <div className="glass-card p-5 sm:p-6" data-testid="files-section">
              <p className="label-eyebrow mb-4">Attached files · {trip.attachments?.length || 0}</p>
              {(!trip.attachments || trip.attachments.length === 0) ? (
                <p className="text-sm text-zinc-500 text-center py-8">No files attached to this record.</p>
              ) : (
                <div className="space-y-2">
                  {trip.attachments.map((f) => (
                    <div key={f.id} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                      <FileText className="h-4 w-4 text-[#F07D00]" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white truncate">{f.filename}</p>
                        <p className="text-xs text-zinc-500">{Math.round(f.size / 1024)} KB · {format(new Date(f.uploaded_at), "MMM d, yyyy")}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function DetailPanel({ icon: Icon, title, children }) {
  return (
    <div className="glass-card p-5" data-testid={`panel-${title.toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4 text-[#F07D00]" />
        <h3 className="font-heading text-sm font-semibold tracking-wider uppercase text-zinc-300">{title}</h3>
      </div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function Row({ label, value, icon: Icon }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <span className="text-zinc-500 min-w-[84px] text-xs uppercase tracking-wider mt-0.5">{label}</span>
      <span className="text-white flex-1 break-words flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3 text-zinc-500" />} {value}
      </span>
    </div>
  );
}

function DriverSection({ trip, onSaved }) {
  const [form, setForm] = useState({
    driver_name: trip.driver_name || "",
    driver_phone: trip.driver_phone || "",
    driver_vehicle: trip.driver_vehicle || "",
    driver_license_plate: trip.driver_license_plate || "",
    driver_notes: trip.driver_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.driver_name.trim()) {
      toast.error("Driver name is required");
      return;
    }
    setSaving(true);
    try {
      await assignDriver(trip.id, form);
      toast.success(trip.driver_name ? "Driver updated" : "Driver assigned");
      await onSaved();
    } catch (e) {
      toast.error("Failed to save driver");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove assigned driver from this trip?")) return;
    setRemoving(true);
    try {
      await unassignDriver(trip.id);
      setForm({ driver_name: "", driver_phone: "", driver_vehicle: "", driver_license_plate: "", driver_notes: "" });
      toast.success("Driver unassigned");
      await onSaved();
    } catch (e) {
      toast.error("Failed to unassign");
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="glass-card p-5 sm:p-6" data-testid="driver-section">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Car className="h-4 w-4 text-[#F07D00]" />
          <h2 className="font-heading text-lg font-medium text-white">Ground transport & driver</h2>
        </div>
        {trip.driver_name ? (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-semibold">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-slow-pulse" /> Assigned
          </div>
        ) : (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-semibold">
            <AlertCircle className="h-3 w-3" /> No driver
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="label-eyebrow flex items-center gap-1">Driver <span className="text-[#F07D00]">*</span></Label>
          <Select value={form.driver_name || ""} onValueChange={(v) => update("driver_name", v)}>
            <SelectTrigger
              className="glass-input mt-2 h-10 [&>span]:text-white"
              data-testid="driver-name-select"
            >
              <SelectValue placeholder="Choose a driver from the roster" />
            </SelectTrigger>
            <SelectContent className="bg-[#0B0C10]/95 backdrop-blur-2xl border-white/10 text-white">
              {DRIVERS.map((d) => (
                <SelectItem key={d} value={d} className="focus:bg-[#F07D00]/15 focus:text-white" data-testid={`driver-option-${d.toLowerCase()}`}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-eyebrow">Phone / WhatsApp</Label>
          <Input
            value={form.driver_phone}
            onChange={(e) => update("driver_phone", e.target.value)}
            placeholder="+52-81-8144-0210"
            className="glass-input mt-2"
            data-testid="driver-phone-input"
          />
        </div>
        <div>
          <Label className="label-eyebrow">Vehicle</Label>
          <Input
            value={form.driver_vehicle}
            onChange={(e) => update("driver_vehicle", e.target.value)}
            placeholder="Suburban 2024 · Negro"
            className="glass-input mt-2"
            data-testid="driver-vehicle-input"
          />
        </div>
        <div>
          <Label className="label-eyebrow">License plate</Label>
          <Input
            value={form.driver_license_plate}
            onChange={(e) => update("driver_license_plate", e.target.value)}
            placeholder="NL-8432-MTY"
            className="glass-input mt-2"
            data-testid="driver-plate-input"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="label-eyebrow">Pickup / route notes</Label>
          <Textarea
            value={form.driver_notes}
            onChange={(e) => update("driver_notes", e.target.value)}
            placeholder="Pickup in MTY T1 · confirmar llegada · ruta diaria hotel ↔ oficina"
            className="glass-input mt-2 min-h-20"
            data-testid="driver-notes-input"
          />
        </div>
      </div>

      <div className="flex items-center justify-between pt-5 mt-5 border-t border-white/10">
        {trip.driver_name ? (
          <Button
            variant="ghost"
            onClick={remove}
            disabled={removing || saving}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            data-testid="driver-unassign-button"
          >
            <Trash2 className="h-4 w-4 mr-1.5" /> Unassign
          </Button>
        ) : (
          <span />
        )}
        <Button
          onClick={save}
          disabled={saving || removing || !form.driver_name.trim()}
          className="brand-btn"
          data-testid="driver-save-button"
        >
          <Save className="h-4 w-4 mr-1.5" /> {trip.driver_name ? "Update driver" : "Assign driver"}
        </Button>
      </div>
    </div>
  );
}
